import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {
    DialogActionsEnum,
    DialogComponent,
    DialogConfigInterface,
    Pagination,
    SelectOptionInterface
} from 'appShared/components';
import {FormInterface, PermissionRuleInterface} from 'appShared/interfaces';
import {Subscription} from 'rxjs';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {
    ApiError,
    ApiResponse,
    BrokerSearchInputModel,
    BrokerSearchOutputInterface,
    BrokerSearchOutputItemModel,
    BrokerSearchOutputModel,
    HttpStatusCodesEnum,
    OrderRouteService,
    RouteSearchInputModel,
    RouteSearchOutputInterface,
    RouteSearchOutputItemModel,
    RouteSearchOutputModel, RouteSetGetOutputModel, ServerResponseData
} from '@spa/api';
import {Router} from '@angular/router';
import {AlertsService} from 'appShared/services';
import {PermissionRulesTrait} from 'appEntries/panel/traits';
import {AlertTypesEnum, ColorCodesEnum, ComponentTypesEnum, PermissionRuleCheckStrategiesEnum} from 'appShared/enums';
import {FormComponent} from 'appShared/widgets';
import {PanelOrderRoutesMessages} from 'appAssets/messages';
import {OrderRoutesFormErrorStatesEnum, PanelOrderRoutesResolverErrorStatesEnum} from 'appEntries/panel/enums';
import {RouteSetForm} from 'appEntries/panel/forms';
import {CheckboxControl, Helper, SelectControl} from 'appShared/classes';
import {FormArray, FormControl} from '@angular/forms';
import {switchMap} from 'rxjs/internal/operators';
import {EventsService} from 'appEntries/panel/services';


export interface RouteSetSelectedRouteInterface {
    brokerId: number;
    brokerResult: BrokerSearchOutputInterface;
}

@Component({
    selector: 'app-add-route-set',
    templateUrl: './add-route-set.component.html',
    styleUrls: ['./add-route-set.component.scss']
})
export class AddRouteSetComponent extends DialogComponent<AddRouteSetComponent> implements FormInterface, OnDestroy, OnInit {

    protected subscriptions: Subscription[];
    public hideBroker: boolean;
    public permissionRule: PermissionRuleInterface;
    public form: RouteSetForm;
    public brokerId: number;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            config: DialogConfigInterface,
            brokerId: number,
            hideBroker: boolean,
        },
        protected router: Router,
        protected alertsService: AlertsService,
        protected orderRouteService: OrderRouteService,
        protected eventsService: EventsService,
        public dialogRef: MatDialogRef<AddRouteSetComponent>,
    ) {
        super(dialogRef);
        this.subscriptions = [];
        this.permissionRule = PermissionRulesTrait.getOrderRoutesCreatePermissionRule(PermissionRuleCheckStrategiesEnum.Union);
        this.config = data.config;
        this.brokerId = data.brokerId;
        this.hideBroker = data.hideBroker;
    }

    private LoadBrokers(brokerFilter: BrokerSearchInputModel) {
        this.orderRouteService.searchBrokers(brokerFilter.serialize()).pipe(
            switchMap((brokerResponse: ServerResponseData<BrokerSearchOutputInterface>) => {
                if (brokerResponse.status !== HttpStatusCodesEnum.Ok) {
                    this.stopLoading();

                    return Helper.errorsHelper().createErrorResponseObservable<BrokerSearchOutputInterface>(
                        brokerResponse,
                        PanelOrderRoutesResolverErrorStatesEnum.GetBrokerSearchExtendedFailed
                    );
                }
                const brokerResult = brokerResponse.data as BrokerSearchOutputInterface;
                const selectedBrokerId = this.brokerId ?? (brokerResult.total_elements === 1 ? brokerResult.content[0].id : null);
                this.stopLoading();

                return Helper.createSuccessResponseObservable<RouteSetSelectedRouteInterface>({
                    brokerId: selectedBrokerId,
                    brokerResult
                });

            })
        ).subscribe(response => {
            if (response.status !== HttpStatusCodesEnum.Ok) {
                this.stopLoading();

                return Helper.errorsHelper().createErrorResponseObservable<RouteSetSelectedRouteInterface>(
                    response,
                    PanelOrderRoutesResolverErrorStatesEnum.GetBrokerSearchExtendedFailed
                );
            }
            const result = response.data as RouteSetSelectedRouteInterface;
            const brokerResult = new BrokerSearchOutputModel(result.brokerResult);
            const selectControl = this.form.controls.broker as SelectControl;
            selectControl.options = brokerResult.content.map(item =>
                ({value: item.getId(), label: item.getName()} as SelectOptionInterface));
            const formControl = this.form.formGroup.get(selectControl.name);
            formControl.patchValue(result.brokerId);
            this.stopLoading();
        });
    }

    handleErrorResponse(response: ApiResponse<ApiError[] | RouteSetGetOutputModel>, error: ApiError): string {
        if (Array.isArray(response.messages) && response.messages[0]) {
            return response.messages[0];
        }

        return this.getErrorMessageByApiError(error);
    }

    onFormSubmit(action?: number): void {
        if (action !== DialogActionsEnum.Confirm) {
            this.dialogRef.close(false);

            return;
        }

        if (this.validateForm()) {
            const requestSubscription = this.form.createRequest()
                .subscribe((response: ServerResponseData<RouteSetGetOutputModel>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        (response.data as ApiError[]).map((error: ApiError) => this.handleErrorResponse(response, error))
                            .filter((errorMessage: string) => {
                                return errorMessage.trim();
                            }).forEach((errorMessage: string) => {
                            this.alertsService.addAlert(errorMessage);
                        });

                        return;
                    }
                    const result = response.data as RouteSetGetOutputModel;
                    this.alertsService.addAlert(this.getSuccessFormMessage(), AlertTypesEnum.Success);
                    this.eventsService.forceExecutionRouteSetGet.next({routeSet: result});
                    this.dialogRef.close(true);
                });
            this.subscriptions.push(requestSubscription);
        }
    }

    getFormMessages(controlLabel?: string): { [p: string]: string } {
        return FormComponent.getFormMessages(controlLabel);
    }

    validateForm(): boolean {
        return this.form.validateForm(this.getFormMessages);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((subscription: Subscription) => subscription && subscription.unsubscribe());
    }

    ngOnInit(): void {
        this.initForm();

        const brokerFilter = new BrokerSearchInputModel(
            Pagination.defaultPagesChunk,
            Pagination.maxInt32PerPage,
            BrokerSearchOutputItemModel.getNameSelector(),
            false,
            null,
            null,
            null,
            null,
            null,
            null
        );
        this.dialogRef.afterOpened().subscribe(() => {
            this.startLoading(() => {
                if (this.brokerId != null && this.hideBroker) {
                    const selectControl = this.form.controls.broker as SelectControl;
                    selectControl.options = [{value: this.brokerId, label: this.brokerId.toString()}];
                    const formControl = this.form.formGroup.get(selectControl.name);
                    formControl.patchValue(this.brokerId);
                    this.stopLoading();
                } else {
                    this.LoadBrokers(brokerFilter);
                }

            });
        });
    }

    handleBrokerChange($event) {
        const routeFormArray = this.form.formGroup.get(this.form.routeSetFormControlsRouteArraySelector) as FormArray;
        routeFormArray.clear();
        if (!$event) {
            return;
        }
        const routeFilter = new RouteSearchInputModel(
            Pagination.defaultPagesChunk,
            Pagination.maxInt32PerPage,
            RouteSearchOutputItemModel.getDisplayNameSelector(),
            false,
            null,
            null,
            null,
            $event,
            null,
            null,
            null
        );

        this.orderRouteService.searchRoutes(routeFilter.serialize())
            .subscribe((response: ServerResponseData<RouteSearchOutputInterface>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse<RouteSearchOutputInterface>(response);
                }
                const routeResult = new RouteSearchOutputModel(response.data as RouteSearchOutputInterface);
                this.form.controls.routes = routeResult.content.map((r: RouteSearchOutputItemModel, index: number) => {
                    return new CheckboxControl({
                        name: index + '',
                        value: r.getId(),
                        placeholder: r.getDisplayName(),
                        nonCapitalizedPlaceholder: true,
                        validators: [],
                        componentType: ComponentTypesEnum.Checkbox,
                        checked: false,
                        color: ColorCodesEnum.Primary,
                        expectBooleanValue: false
                    });
                });
                this.form.controls.routes.forEach((checkbox: CheckboxControl) => {
                    const checkboxFormControl = new FormControl(checkbox.value, checkbox.validators);
                    routeFormArray.push(checkboxFormControl);
                });
            });
    }

    initForm(): void {
        this.form = new RouteSetForm(this.orderRouteService);
        this.form.initForm();
    }

    getSuccessFormMessage(): string {
        return PanelOrderRoutesMessages.RouteSetCreationSucceed.message;
    }

    getErrorMessageByApiError(error: ApiError): string {
        if (error.status === OrderRoutesFormErrorStatesEnum.CreateRouteSetFailed) {
            return PanelOrderRoutesMessages.RouteSetCreationFailed.message;
        }

        return PanelOrderRoutesMessages.UnknownError.message;
    }
}
